import BoldText from '@/components/BoldText';
import CriticalBanner from '@/components/CriticalBanner';
import CustomCard from '@/components/customCard';
import { OnboardingTrackingStep, QuickTemplateType, Rules, TypePreference } from '@/constants/enum';
import { PATH } from '@/constants/path';
import { checkShowErrorInline, handleToastMutation } from '@/helpers';
import { apiCaller } from '@/redux/query';
import { isSkipApiSelector } from '@/redux/slice/auth.slice';
import onboardingSlice, { onboardingSelector } from '@/redux/slice/onboarding.slice';
import sessionSlice, { onboardingStepSelector } from '@/redux/slice/session.slice';
import settingsSlice, { dataSettingsSelector, ruleSelector, shopInfoSelector } from '@/redux/slice/settings.slice';
import toastSlice from '@/redux/slice/toast.slice';
import { BlockStack, Button, Page, ProgressBar, Tooltip } from '@shopify/polaris';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Step1 from './Step1';
import Step3 from './Step3';
import Step4 from './Step4';
import { OnboardingStyled } from './styled';
import useUserPlan from '@/hooks/useUserPlan';
import Step2 from '@/pages/Onboarding/Step2';
import { IRule } from '@/types/newSetup.type';
import { IParamsApi } from '@/types/apis/params';
import { XIcon } from '@shopify/polaris-icons';

export enum RuleTypeEnum {
  Checkout = 'checkout',
  Storefront = 'store-front',
}

export interface IProps {
  handleNext: () => void;
  handleFinish: (isDisabledToHome?: boolean) => void;
  isDisabled: boolean;
  isShowError?: boolean;
  setRuleType?: React.Dispatch<React.SetStateAction<RuleTypeEnum | undefined>>;
  ruleType?: RuleTypeEnum;
  isEmbedded?: boolean;
}

const OnBoarding = () => {
  const { isActivePlusPlan } = useUserPlan();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const onboarding = useSelector(onboardingSelector);
  const dataSettings = useSelector(dataSettingsSelector);
  const rule = useSelector(ruleSelector);
  const shopInfo = useSelector(shopInfoSelector);
  const isSkip = useSelector(isSkipApiSelector);
  const onboardingStep = useSelector(onboardingStepSelector);
  const warningRef = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState(0);
  const [step, setStep] = useState(1);
  const { limitPlan } = useUserPlan();

  const [trackOnboarding] = apiCaller.useTrackOnboardingMutation();
  const [updateOnboarding, { isLoading }] = apiCaller.useUpdateOnboardingMutation();
  const [updateGeneralSetting, { isLoading: isLoadingUpdateGeneralSetting }] = apiCaller.useUpdateGeneralSettingMutation();
  const [createRule] = apiCaller.useCreateRuleMutation();
  const [createCheckoutRule] = apiCaller.useCreateCheckoutRuleMutation();
  const { data: totalRule } = apiCaller.useRuleCountQuery(undefined, { skip: isSkip });
  // const [sendEventCustomerIo] = apiCaller.useSendEventCustomerIoMutation();
  const { data: dataEmbeddedAppRes, refetch: refetchDataEmbeddedApp } = apiCaller.useGetEmbeddedAppStatusQuery(undefined, {
    skip: isSkip,
  });
  const [ruleType, setRuleType] = useState<RuleTypeEnum | undefined>();
  const { data: dataEmbeddedApp } = dataEmbeddedAppRes || {};

  // Thêm event listener để theo dõi visibility change để gọi api get app embedded
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refetchDataEmbeddedApp();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refetchDataEmbeddedApp]);

  const isReachedLimitRule = useMemo(() => {
    return !!limitPlan && !!totalRule?.data?.countRule && totalRule?.data?.countRule >= limitPlan.maxRule;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalRule?.data?.countRule, limitPlan?.maxRule]);

  useEffect(() => {
    if (!dataSettings.displayOnboarding) {
      navigate(PATH.DEFAULT);
    }
  }, [dataSettings.displayOnboarding, navigate]);

  useEffect(() => {
    handleTrackOnboarding(OnboardingTrackingStep.Step1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let res = 0;
    switch (onboarding.step_active) {
      case 1:
        res = 3;
        break;
      case 2:
        res = 35;
        break;
      case 3:
        res = isActivePlusPlan ? 68 : 48;
        break;
      case 4:
        res = 100;
        break;
      default:
        break;
    }
    setProgress(res);
  }, [onboarding.step_active, isActivePlusPlan]);

  const handleTrackOnboarding = useCallback(
    (step: OnboardingTrackingStep) => {
      if (isSkip || onboardingStep[step]) return;
      const type_preference =
        rule === Rules.Methods
          ? TypePreference.Method
          : rule === Rules.PostCode
          ? TypePreference.PostCode
          : TypePreference.Country;
      trackOnboarding({
        current_step: step,
        type_preference: type_preference,
        template_preference: QuickTemplateType.Basic,
      });
      dispatch(sessionSlice.actions.handleOnboardingStep(step));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(rule), onboardingStep, isSkip, trackOnboarding],
  );

  const handleChangeStep = (step: number) => {
    setStep(step);
    dispatch(
      onboardingSlice.actions.handleUpdateOnboarding({
        ...onboarding,
        step_active: step,
        error: { status: false, msg: '' },
      }),
    );
    switch (step) {
      case 1:
        handleTrackOnboarding(OnboardingTrackingStep.Step1);
        break;
      case 2:
        handleTrackOnboarding(OnboardingTrackingStep.Step2);
        break;
      case 3:
        handleTrackOnboarding(OnboardingTrackingStep.Step3);
        break;
      case 4:
        handleTrackOnboarding(OnboardingTrackingStep.Step4);
        break;
      default:
        break;
    }
  };

  const handleNext = useCallback(
    (input?: { step?: number }) => {
      if (!onboarding.error.status) {
        dispatch(
          onboardingSlice.actions.handleUpdateOnboarding({
            ...onboarding,
            step_active: input?.step ? input.step : onboarding.step_active + 1,
          }),
        );
        window.scrollTo(0, 0);
        warningRef.current && warningRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, JSON.stringify(onboarding)],
  );

  const handleUpdateGeneralSetting = useCallback(
    (data?: { displayOnboarding?: boolean }) => {
      if (isSkip) return Promise.reject();
      const newDataSettings = { ...dataSettings, displayOnboarding: data?.displayOnboarding ?? dataSettings.displayOnboarding };
      return updateGeneralSetting(newDataSettings).then((res) => {
        const checkError = checkShowErrorInline(res);
        if (checkError.status) {
          dispatch(onboardingSlice.actions.handleSetError({ ...checkError, isShowBanner: true }));
          return Promise.reject();
        }
        dispatch(settingsSlice.actions.handleDataSettingsBackup(newDataSettings));
        return Promise.resolve();
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(dataSettings), dispatch, updateGeneralSetting, isSkip],
  );

  const handleSaveRules = useCallback(() => {
    if (
      (!onboarding.isAllowedSaveRule && ruleType !== RuleTypeEnum.Checkout) ||
      (!onboarding.isAllowedSaveCheckoutRule && ruleType === RuleTypeEnum.Checkout)
    ) {
      handleNext();
      return Promise.resolve();
    }
    if (isSkip) return Promise.reject();

    const createMutation = () => {
      if (ruleType === RuleTypeEnum.Checkout) {
        return (data: IParamsApi.ICreateCheckoutRule) => createCheckoutRule(data);
      }
      return (data: IRule) => createRule(data);
    };

    const mutation = createMutation();

    const newShippingMethods = onboarding.checkoutRule.shippingMethods.map((item) => item.value);

    const dataCheckoutRule = {
      ...onboarding.checkoutRule,
      enable: false,
      shippingMethods: newShippingMethods,
      minPreparationDays: +onboarding.checkoutRule.minPreparationDays,
      maxPreparationDays: +onboarding.checkoutRule.maxPreparationDays,
      minDeliveryDays: +onboarding.checkoutRule.minDeliveryDays,
      maxDeliveryDays: +onboarding.checkoutRule.maxDeliveryDays,
    } as IParamsApi.ICreateCheckoutRule;

    // remove locationConditions
    const newRule =
      ruleType === RuleTypeEnum.Checkout ? dataCheckoutRule : ({ ...onboarding.rule, locationConditions: [] } as IRule);
    //@ts-ignore-next-line
    return mutation(newRule).then((res) => {
      const checkError = checkShowErrorInline(res);
      if (checkError.status) {
        dispatch(onboardingSlice.actions.handleSetError({ ...checkError, isShowBanner: true }));
        window.scrollTo(0, 0);
        return Promise.reject();
      } else {
        const newOnboardingState = {
          ...onboarding,
          step_active: onboarding.step_active + 1,
        };
        if (ruleType === RuleTypeEnum.Checkout) {
          newOnboardingState.isAllowedSaveCheckoutRule = false;
        } else {
          newOnboardingState.isAllowedSaveRule = false;
        }
        dispatch(toastSlice.actions.handleToast(handleToastMutation(res)));
        dispatch(onboardingSlice.actions.handleUpdateOnboarding(newOnboardingState));
        return Promise.resolve();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createRule, dispatch, handleNext, JSON.stringify(onboarding), isSkip, ruleType]);

  const handleUpdateOnboarding = useCallback(
    (step: OnboardingTrackingStep) => {
      if (!shopInfo?.email || isSkip) return;
      updateOnboarding({
        isAgreeConsent: true,
        email: shopInfo.email,
      }).then((res) => {
        const checkError = checkShowErrorInline(res);
        if (checkError.status) {
          dispatch(onboardingSlice.actions.handleSetError(checkError));
          window.scrollTo(0, 0);
          return Promise.reject();
        } else if (!onboarding.error.status) {
          dispatch(toastSlice.actions.handleToast(handleToastMutation(res)));
          handleTrackOnboarding(step);
          if (step === OnboardingTrackingStep.Finish) {
            navigate(PATH.DEFAULT, { replace: true });
          }
          return Promise.resolve();
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, handleTrackOnboarding, navigate, onboarding.error.status, shopInfo?.email, updateOnboarding],
  );

  const handleFinish = useCallback(
    async (input: { isSaveRules: boolean }) => {
      try {
        if (input.isSaveRules) {
          await handleSaveRules();
        }
        await handleUpdateOnboarding(OnboardingTrackingStep.Finish);
        await handleUpdateGeneralSetting({ displayOnboarding: false });
        // if (isReachedLimitRule || !onboarding.isAllowedSaveRule || !onboarding.isAllowedSaveCheckoutRule) {
        //   sendEventCustomerIo({ event: 'onboard_done' });
        // }
      } catch (error) {
        console.error('An error occurred:', error);
        // Handle the error here, such as displaying an error message to the user
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleSaveRules, handleUpdateGeneralSetting, handleUpdateOnboarding, JSON.stringify(rule), step],
  );

  const onboardingConfig = useMemo(() => {
    return [
      {
        title: 'Welcome',
        step: 1,
        component: (
          <Step1
            isDisabled={isLoading}
            handleNext={() => {
              // next step
              handleNext({ step: isActivePlusPlan ? 2 : 3 });
              handleTrackOnboarding(OnboardingTrackingStep.Step3);
            }}
            handleFinish={() => {
              handleFinish({ isSaveRules: false });
            }}
          />
        ),
      },
      isActivePlusPlan
        ? {
            title: 'Choose type',
            step: 2,
            component: (
              <Step2
                isDisabled={isLoading}
                handleNext={() => {
                  handleNext({ step: 3 });
                  handleTrackOnboarding(OnboardingTrackingStep.Step3);
                }}
                handleFinish={() => {
                  handleFinish({ isSaveRules: false });
                }}
                setRuleType={setRuleType}
                ruleType={ruleType}
              />
            ),
          }
        : undefined,
      {
        title: 'Set up',
        step: 3,
        component: (
          <Step3
            isDisabled={
              isLoading ||
              isLoadingUpdateGeneralSetting ||
              onboarding.error.status ||
              (!onboarding.isAllowedSaveRule && ruleType !== RuleTypeEnum.Checkout) ||
              isReachedLimitRule ||
              (!onboarding.isAllowedSaveCheckoutRule && ruleType === RuleTypeEnum.Checkout)
            }
            isShowError={
              ruleType === RuleTypeEnum.Checkout
                ? !onboarding.isAllowedSaveCheckoutRule
                : !onboarding.isAllowedSaveRule || isReachedLimitRule
            }
            handleNext={() => {
              handleUpdateGeneralSetting()
                .then(handleSaveRules)
                .then(() => {
                  // next step
                  handleUpdateOnboarding(OnboardingTrackingStep.Step4);
                })
                .catch((error) => console.log(error));
            }}
            handleFinish={() => {
              handleFinish({ isSaveRules: false });
            }}
            ruleType={ruleType}
          />
        ),
      },
      {
        title: 'Embed and view',
        step: 4,
        component: (
          <Step4
            isDisabled={isLoading}
            handleNext={() => {}}
            handleFinish={() => {
              handleFinish({ isSaveRules: false });
            }}
            ruleType={ruleType}
            isEmbedded={dataEmbeddedApp?.disabled}
          />
        ),
      },
    ].filter((item) => !!item);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    handleFinish,
    handleNext,
    handleSaveRules,
    handleTrackOnboarding,
    handleUpdateGeneralSetting,
    handleUpdateOnboarding,
    isLoading,
    isLoadingUpdateGeneralSetting,
    onboarding.error.status,
    onboarding.isAllowedSaveRule,
    onboarding.isAllowedSaveCheckoutRule,
    setRuleType,
    ruleType,
  ]);

  return (
    <OnboardingStyled>
      <Page title={''} fullWidth>
        <div className="page-content">
          <CustomCard hasDroplist>
            <BlockStack gap={'200'}>
              <div className="d-flex">
                <div className="block-header w-full">
                  <ProgressBar progress={progress} animated={true} size="small" />
                  <div className="step-title-container">
                    {onboardingConfig.map((item) => (
                      <div
                        className={`step-title ${item.step === onboarding.step_active ? 'active' : ''}`}
                        key={item.step}
                        onClick={() => {
                          handleChangeStep(item.step);
                        }}
                      >
                        <BoldText>{item.title}</BoldText>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <Tooltip content="Dismiss">
                    <Button variant="plain" icon={XIcon} onClick={() => handleFinish({ isSaveRules: false })} />
                  </Tooltip>
                </div>
              </div>

              <div>
                <div className="mt-8" ref={warningRef}>
                  <CriticalBanner isVisible={onboarding.error.status && !!onboarding.error.isShowBanner}>
                    {onboarding.error.msg}
                  </CriticalBanner>
                </div>
                <div>{onboardingConfig.find((item) => item.step === onboarding.step_active)?.component}</div>
              </div>
            </BlockStack>
          </CustomCard>
        </div>
      </Page>
    </OnboardingStyled>
  );
};

export default memo(OnBoarding);
